import {
  DIValidators,
  DIMixins,
  EFeatureFlags,
  EFinancasModulos,
  F360Product,
  FinancasUserRoles,
  IF360Sidebar
} from "@/core/@types/types";
import F360Sidebar from "@/core/common/F360AbstractSidebar";
import { IndicadoresDashboardRouter } from "@/features/financas/views/Indicadores/router/dashboard/types";
import LogoIconBlue from "@/ui/assets/f360-icon-blue.png";
import { inject, injectable } from "inversify";
import { EFinancasRoutes } from "@/core/router/financas/credenciamento/types";
import { useRegraModulosCielo } from "@/core/common/useRegraModulosCielo";
import { EminhaGestao } from "@/features/financas/views/minhaGestao/router/types";
import { type IF360FeatureFlagValidator } from "@/core/validators/F360FeatureFlagValidator";
import { type IF360Validator } from "@/core/validators/F360Validator";
import { type IUserProfileStorageMixin } from "@/core/plugins/storage/UserProfileStorageMixin";
import { EMenuAdmin } from "./enum/MenuAdmin";
import { EAdminViewName } from "@/features/admin/routes/types";
import { EIntegracoes } from "@/features/financas/views/Integracoes/router";
import { EInsightsIARoutes } from "@/features/financas/views/InsightsIA/router";
import InsightsIAIcon from "@/features/shared/InsightsIAIcon.vue";

@injectable()
export default class F360FinancasSidebarConfig extends F360Sidebar {
  private readonly regraModulosCielo = useRegraModulosCielo();

  private readonly hideOnCielo: boolean =
    this.regraModulosCielo?.hideModulos || false;

  @inject(DIValidators.FEATURE_FLAGS)
  private readonly featureFlag!: IF360FeatureFlagValidator;

  @inject(DIValidators.ADMIN_GESTAO_CONTRATOS)
  private readonly adminGestaoContratosValidator!: IF360Validator;

  @inject(DIMixins.USER_PROFILE_STORAGE)
  private readonly userProfileStorage!: IUserProfileStorageMixin;

  @inject(DIValidators.FEATURE_FLAG_ADMIN)
  private readonly featureFlagAdminValidator!: IF360Validator;

  private readonly isLight: boolean = this.regraModulosCielo?.isLight || false;

  /** Perfil migração (B): `MinhaGestaoMigracao` em `Features` (alinhado ao `FeatureFlagValidator`). */
  private isMinhaGestaoMigracaoPerfil(): boolean {
    try {
      return this.featureFlag.handleValidationByName(
        EFeatureFlags.MinhaGestaoMigracao
      );
    } catch {
      return false;
    }
  }

  private validatorModulos(): boolean {
    if (
      this.hideOnCielo === false &&
      this.regraModulosCielo?.modulo === EFinancasModulos.COMPLETO
    ) {
      return true;
    }

    return this.hideOnCielo && this.isLight;
  }

  private canAccessRegrasCO(): boolean {
    const email = this.userProfileStorage
      .handleGetUserProfile()?.Email?.toLowerCase();

    return (
      this.isSysAdmin() &&
      ["alex@financas360.com.br", "vanessa.zanco@financas360.com.br"].includes(
        email || ""
      )
    );
  }

  private canAccessGestaoContratosAdmin(): boolean {
    return (
      this.isSysAdmin() && this.adminGestaoContratosValidator.handleValidation()
    );
  }

  private canAccessFeatureFlagsAdmin(): boolean {
    return (
      this.isSysAdmin() && this.featureFlagAdminValidator.handleValidation()
    );
  }

  private getCentroDeCustoRouterConfig(): IF360Sidebar["routerConfig"] {
    const hasFeatureFlag = this.featureFlag.handleValidationByName(
      EFeatureFlags.CentroDeCusto
    );

    if (!hasFeatureFlag) {
      return {
        product: F360Product.Financas,
        routeName: this.handleFinancasURL("CentroDeCusto")
      };
    }

    return async () => {
      window.location.hash = "/CentroDeCusto";
    };
  }

  private getRegraDeRateioRouterConfig(): IF360Sidebar["routerConfig"] {
    const hasFeatureFlag = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaRegraDeRateio
    );

    if (!hasFeatureFlag) {
      return {
        product: F360Product.Financas,
        routeName: this.handleFinancasURL("RegraDeRateio")
      };
    }

    return async () => {
      window.location.hash = "/RegraDeRateio";
    };
  }

  private getClientesFornecedoresRouterConfig(): IF360Sidebar["routerConfig"] {
    const hasFeatureFlag = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaClientesFornecedores
    );

    if (!hasFeatureFlag) {
      return {
        product: F360Product.Financas,
        routeName: this.handleFinancasURL("Pessoa")
      };
    }

    return async () => {
      window.location.hash = "/clientes-fornecedores";
    };
  }

  private handleMenuGestao(): Array<IF360Sidebar> {
    const hasMinhaGestao = this.featureFlag.handleValidationByName(
      EFeatureFlags.MinhaGestao
    );
    const hasIntegracoes = this.featureFlag.handleValidationByName(
      EFeatureFlags.Integracoes
    );
    const hasRelatorios = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaRelatorio
    );
    const credenciamentoLiberado =
      this.userProfileStorage.handleGetUserProfile()?.CredenciamentoLiberado ||
      false;

    return [
      {
        menuLabel: this.isCielo() ? "Menu de Gestão" : "Finanças",
        img: LogoIconBlue,
        isGrouped: true,
        validator: [!this.isSysAdmin()],
        children: [
          {
            menuLabel: "Cadastros",
            icon: "editor_choice",
            showGameficacao: true,
            routerConfig: {
              product: F360Product.MinhaGestao,
              routeName: this.isMinhaGestaoMigracaoPerfil()
                ? EminhaGestao.Empresas
                : EminhaGestao.Index,
              hasSubRoutes: true
            },
            validator: [this.isCielo(), hasMinhaGestao]
          },
          {
            menuLabel: "Empresas e Filiais",
            icon: "business_center",
            roles: ["Empresa.Index"],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Empresa")
            },
            validator: [!hasMinhaGestao, !this.isCielo()]
          },
          {
            menuLabel: "Credenciamento",
            icon: "credit_card",
            roles: ["CredenciamentoAdquirentes.Index"],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("CredenciamentoAdquirentes")
            },
            validator: [
              !hasMinhaGestao,
              credenciamentoLiberado,
              !this.isCielo()
            ]
          },
          {
            menuLabel: "Contas",
            icon: "account_balance",
            roles: ["Conta.Index"],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Conta")
            },
            validator: [!hasMinhaGestao, !this.isCielo()]
          },
          {
            menuLabel: "Dashboard",
            icon: "monitoring",
            routerConfig: {
              product: F360Product.Indicadores,
              routeName: IndicadoresDashboardRouter.Index
            },
            roles: [FinancasUserRoles.Dashboard]
          },
          {
            menuLabel: "Fechamento de caixa",
            icon: "calendar_month",
            roles: [FinancasUserRoles.FechamentoDeCaixa],
            validator: [!this.isLight],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("FechamentoDeCaixa")
            }
          },
          {
            menuLabel: !this.hideOnCielo ? "Conciliação de cartões" : "Cartões",
            icon: "credit_card",
            roles: [FinancasUserRoles.Conciliacao],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Conciliacao")
            }
          },
          {
            menuLabel: !this.hideOnCielo
              ? "Contas a receber"
              : "Registro de Vendas",
            icon: "add_circle",
            roles: [FinancasUserRoles.Receita],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Receita")
            }
          },
          {
            menuLabel: "Contas a pagar",
            icon: "do_not_disturb_on",
            roles: [FinancasUserRoles.Despesa],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Despesa")
            }
          },
          {
            menuLabel: "Nota Fiscal Eletrônica",
            icon: "barcode",
            roles: [FinancasUserRoles.Nfe],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("NFe")
            }
          },
          {
            menuLabel: "Plano orçamentário",
            icon: "hourglass_top",
            roles: [FinancasUserRoles.PlanoOrcamentario],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("PlanoOrcamentario")
            }
          },
          {
            menuLabel: "Fluxo de caixa",
            icon: "point_of_sale",
            roles: [FinancasUserRoles.FluxoDeCaixa],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("FluxoDeCaixa")
            }
          },
          {
            menuLabel: "Conciliação de contas",
            icon: "account_balance",
            roles: [FinancasUserRoles.ContasMovimento],
            validator: [!this.isLight],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("ContasMovimento")
            }
          },
          {
            menuLabel: "DRE",
            icon: "paid",
            roles: [FinancasUserRoles.DRE],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("DRE")
            }
          },
          {
            menuLabel: "Integrações",
            icon: "electrical_services",
            routerConfig: {
              product: F360Product.Integracoes,
              routeName: EIntegracoes.Index,
              hasSubRoutes: true
            },
            validator: [
              hasIntegracoes
            ]
          },
          {
            menuLabel: "Integrações",
            icon: "electrical_services",
            roles: ["Webservice.Index"],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Webservice")
            },
            validator: [!hasIntegracoes, !this.isCielo()]
          },
          {
            menuLabel: "Diagnóstico",
            icon: "cardiology",
            roles: [FinancasUserRoles.Diagnostico],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Diagnostico")
            }
          },
          {
            menuLabel: "Insights IA",
            showBagde: true,
            iconComponent: InsightsIAIcon,
            iconClass: "f360-menu-icon-svg",
            roles: [FinancasUserRoles.DRE],
            validator: [
              !this.hideOnCielo,
              this.featureFlag.handleValidationByName(EFeatureFlags.InsightsIA)
            ],
            routerConfig: {
              product: F360Product.FinancasNewHorizons,
              routeName: EInsightsIARoutes.AgenteDeDRE
            }
          },
          {
            menuLabel: "Upload de arquivos",
            icon: "cloud_upload",
            roles: [FinancasUserRoles.Arquivo],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Arquivo")
            }
          },
          {
            menuLabel: "Relatórios",
            icon: "table_rows",
            routerConfig: hasRelatorios
              ? {
                  product: F360Product.FinancasNewHorizons,
                  routeName: "Relatorio"
                }
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("Relatorio")
                },
            validator: [!this.isCielo()]
          }
        ]
      }
    ];
  }

  handleMenuConfiguracoes(): Array<IF360Sidebar> {
    const mgMigracao = this.isMinhaGestaoMigracaoPerfil();
    const featureCentroDeCusto = this.featureFlag.handleValidationByName(
      EFeatureFlags.CentroDeCusto
    );
    const featurePlanoDeConta = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaPlanoDeConta
    );
    const featureClientesFornecedores = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaClientesFornecedores
    );
    const featureRegraDeRateio = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaRegraDeRateio
    );
    const featureUsuarios = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaUsuarios
    );
    const featureRegraDeUsuario = this.featureFlag.handleValidationByName(
      EFeatureFlags.NovaTelaRegraDeUsuario
    );

    const directVueRoute = (routeName: string) => ({
      product: F360Product.FinancasNewHorizons,
      routeName
    });

    return [
      {
        menuLabel: "Configurações",
        icon: "settings",
        customClass: "configuracoes-menu",
        isGrouped: true,
        validator: [!this.isSysAdmin(), !this.isCielo()],
        children: [
          {
            menuLabel: "Cadastros",
            icon: "checkbook",
            showGameficacao: true,
            routerConfig: {
              product: F360Product.MinhaGestao,
              routeName: mgMigracao
                ? EminhaGestao.Empresas
                : EminhaGestao.Index,
              hasSubRoutes: true
            },
            validator: [
              this.featureFlag.handleValidationByName(EFeatureFlags.MinhaGestao)
            ]
          },
          {
            menuLabel: "Centros de custo",
            icon: "domain",
            roles: ["CentroDeCusto.Index"],
            routerConfig: featureCentroDeCusto
              ? directVueRoute("CentroDeCusto")
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("CentroDeCusto")
                },
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Clientes e fornecedores",
            icon: "contacts",
            roles: ["Pessoa.Index"],
            routerConfig: featureClientesFornecedores
              ? directVueRoute("clientes-fornecedores")
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("Pessoa")
                },
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Plano de contas",
            icon: "table_chart",
            roles: ["PlanoDeConta.Index"],
            routerConfig: featurePlanoDeConta
              ? directVueRoute("PlanoDeConta")
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("PlanoDeConta")
                },
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Regras de rateio",
            icon: "pie_chart",
            roles: ["RegraDeRateio.Index"],
            routerConfig: featureRegraDeRateio
              ? directVueRoute("RegraDeRateio")
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("RegraDeRateio")
                },
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Regras de usuário",
            icon: "group",
            roles: ["administrador.f360"],
            routerConfig: featureRegraDeUsuario
              ? directVueRoute("RegraDeUsuario")
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("RegraDeUsuario")
                },
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Usuários",
            icon: "groups",
            roles: ["administrador.f360"],
            routerConfig: featureUsuarios
              ? directVueRoute("Usuario")
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("Usuario")
                },
            validator: [!this.hideOnCielo]
          }
        ]
      }
    ];
  }

  private handleMenuCadastro(): Array<IF360Sidebar> {
    const mgMigracao = this.isMinhaGestaoMigracaoPerfil();
    const mgMigracaoValidators = mgMigracao
      ? [
          this.featureFlag.handleValidationByName(EFeatureFlags.MinhaGestao),
          this.featureFlag.handleValidationByName(
            EFeatureFlags.MinhaGestaoMigracao
          )
        ]
      : undefined;

    return [
      {
        menuLabel: "Menu de cadastro",
        isGrouped: true,
        validator: [!this.isSysAdmin(), this.isCielo()],
        children: [
          {
            menuLabel: "Empresas e Filiais",
            routerConfig: mgMigracao
              ? {
                  product: F360Product.MinhaGestao,
                  routeName: EminhaGestao.Empresas,
                  hasSubRoutes: true
                }
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("Empresa")
                },
            icon: "business_center",
            validator: mgMigracaoValidators
          },
          {
            menuLabel: "Credenciamento",
            routerConfig: mgMigracao
              ? {
                  product: F360Product.MinhaGestao,
                  routeName: EminhaGestao.Adquirentes,
                  hasSubRoutes: true
                }
              : {
                  product: F360Product.FinancasNewHorizons,
                  routeName: EFinancasRoutes.credenciamento
                },
            icon: "credit_card",
            validator: mgMigracaoValidators
          },
          {
            menuLabel: "Centros de custo",
            routerConfig: this.getCentroDeCustoRouterConfig(),
            icon: "apartment",
            validator: [!this.hideOnCielo],
            roles: [FinancasUserRoles.CentroDeCusto]
          },
          {
            menuLabel: "Clientes e fornecedores",
            routerConfig: this.getClientesFornecedoresRouterConfig(),
            icon: "contacts",
            validator: [!this.hideOnCielo],
            roles: ["Pessoa.Index"]
          },
          {
            menuLabel: "Plano de contas",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("PlanoDeConta")
            },
            icon: "table_chart",
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Contas",
            icon: "account_balance",
            routerConfig: mgMigracao
              ? {
                  product: F360Product.MinhaGestao,
                  routeName: EminhaGestao.Contas,
                  hasSubRoutes: true
                }
              : {
                  product: F360Product.Financas,
                  routeName: this.handleFinancasURL("Conta")
                },
            validator: mgMigracaoValidators
          },
          {
            menuLabel: "Regras de rateio",
            icon: "pie_chart",
            routerConfig: this.getRegraDeRateioRouterConfig(),
            validator: [!this.hideOnCielo],
            roles: [FinancasUserRoles.RegraDeRateio]
          },
          {
            menuLabel: "Integrações",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Webservice")
            },
            icon: "electrical_services",
            validator: [
              !this.featureFlag.handleValidationByName(
                EFeatureFlags.Integracoes
              )
            ]
          },
          {
            menuLabel: "Upload de arquivos",
            icon: "cloud_upload",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Arquivo")
            }
          },
          {
            menuLabel: "Relatórios",
            icon: "table_rows",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Relatorio")
            }
          }
        ]
      }
    ];
  }

  private handleMenuAdmin(): Array<IF360Sidebar> {
    return [
      {
        menuLabel: "Finanças",
        img: LogoIconBlue,
        isGrouped: true,
        roles: [FinancasUserRoles.FechamentoDeCaixa],
        validator: [this.isSysAdmin()],
        children: [
          {
            menuLabel: EMenuAdmin.CLIENTES,
            icon: "groups",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Cliente")
            }
          },
          {
            menuLabel: EMenuAdmin.USUARIOS,
            icon: "person",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Usuario")
            }
          },
          {
            menuLabel: EMenuAdmin.VAN_CONFIG,
            icon: "group",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("VanConfig")
            }
          },
          {
            menuLabel: EMenuAdmin.LEAD,
            icon: "rocket_launch",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("OnboardingLead")
            }
          },
          {
            menuLabel: EMenuAdmin.INTEGRACOES,
            icon: "integration_instructions",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Integracoes")
            }
          },
          {
            menuLabel: EMenuAdmin.REGRAS_CO,
            icon: "settings",
            validator: [this.canAccessRegrasCO()],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("RegrasCO")
            }
          },
          {
            menuLabel: EMenuAdmin.NOTIFICACOES,
            icon: "notifications",
            routerConfig: {
              product: F360Product.Admin,
              routeName: EAdminViewName.NOTIFICACOES
            }
          },
          {
            menuLabel: EMenuAdmin.FEATURE_FLAGS,
            icon: "toggle_on",
            validator: [this.canAccessFeatureFlagsAdmin()],
            routerConfig: {
              product: F360Product.Admin,
              routeName: EAdminViewName.FEATURE_FLAGS
            }
          },
          {
            menuLabel: EMenuAdmin.GESTAO_CONTRATOS,
            icon: "description",
            validator: [this.canAccessGestaoContratosAdmin()],
            routerConfig: {
              product: F360Product.Admin,
              routeName: EAdminViewName.GESTAO_CONTRATOS
            }
          },
        ]
      }
    ];
  }

  handleSidebar(): Array<IF360Sidebar> {
    return [
      ...this.handleMenuGestao(),
      ...this.handleMenuCadastro(),
      ...this.handleMenuAdmin()
    ];
  }
}
